import Avatar from '@mui/material/Avatar';
import { Body2, Caption } from 'components/shareable/Typography';
import UpgradeToProChip from 'components/shareable/UpgradeToProChip';
import { BrandPermission, CurrentBrand } from "modules/Types";
import { ReactComponent as ChevronDownIcon } from 'assets/images/chevronDown.svg';
import { ReactComponent as TrashIcon } from 'assets/images/trash.svg';
import { MenuItem, Select } from '@material-ui/core';
import { useStyles } from './styles';
import AddBrandsPopUp from '../AddMemberModal/AddBrandsPopUp';
import Colors from 'modules/Colors';
import { PlanType, USER_PERMISSION_ON_BRAND } from 'modules/Const';
import { clsx } from 'modules/Utils';
import useCurrentBrand, { BrandExistsState } from 'Hooks/useCurrentBrand';
import { CSSProperties } from 'react';

const BANDS_PERMISSIONS = {
  administrator: 'Full Access',
  view_only: 'View only',
};

interface BrandsListProps {
  brands: CurrentBrand[];
  canEdit?: boolean;
  isCurrentUser?: boolean;
  onUpdateBrands?: (artists: CurrentBrand[]) => void;
}

const BrandsList = ({
  brands, // NOTE: this is a "temp limited brands list" because it might come from TeamMember.brands, which is an array of brand.ids and limited properties AND we are editing properties like permissionLevel
  canEdit = true,
  isCurrentUser = false,
  onUpdateBrands = () => { },
}: BrandsListProps) => {
  const classes = useStyles();
  const { loadedBrandsDict } = useCurrentBrand() as BrandExistsState;

  const onUpdatePermission = (id: number, permission: BrandPermission) => {
    const updatedBrands = brands.map(brand => brand.id === id ? { ...brand, permissionLevel: permission } : brand);
    onUpdateBrands(updatedBrands);
  };

  const onDeleteBrand = (id: number) => {
    const updatedBrands = brands.filter(brand => brand.id !== id);
    onUpdateBrands(updatedBrands);
  };

  const onAddBrand = (brand: CurrentBrand) => {
    onUpdateBrands([...brands, { ...brand, permissionLevel: USER_PERMISSION_ON_BRAND.ADMINISTRATOR }]);
  }

  const renderBrandPermissionSelect = (brand: CurrentBrand) => {
    return (
      <Select
        className={clsx('brandsListSelectOrg', classes.select)}
        required
        displayEmpty
        value={brand?.permissionLevel}
        onChange={(e) => onUpdatePermission(brand.id, e.target.value as BrandPermission)}
        variant="standard"
        IconComponent={() => canEdit ? <ChevronDownIcon className="h-8 w-8" /> : null}
        disabled={!canEdit}
        MenuProps={{
          getContentAnchorEl: null,
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'center',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'center',
          }
        }}
        renderValue={(value) => {
          if (!value) {
            return <Body2 color={Colors.grey}>Select a permission</Body2>
          }
          return <Body2>{BANDS_PERMISSIONS[value as BrandPermission]}</Body2>
        }}
      >
        <MenuItem className='brandsListMenuItem1' value="administrator">
          <div className='brandsListMenuItem1Container1 flex flex-col gap-1'>
            <Body2 color={Colors.black}>Full Access</Body2>
            <Caption color={Colors.greyDark} style={{ width: 300, textWrap: 'wrap' }}>Full Access permissions allow this teammate to create and edit campaigns & websites.</Caption>
          </div>
        </MenuItem>
        <MenuItem className='brandsListMenuItem2' value="view_only">
          <div className='brandsListMenuItem2Container2 flex flex-col gap-1'>
            <Body2 color={Colors.black}>View Only</Body2>
            <Caption color={Colors.greyDark} style={{ width: 300, textWrap: 'wrap' }}>View-only access to the profile.This teammate will not have the ability to edit or create campaigns or websites.</Caption>
          </div>
        </MenuItem>
      </Select>
    )
  }

  const renderBrandsAccessList = () => {
    if (!brands.length) {
      return (
        <div className="brandsAccessListContainer" style={{ height: 80, backgroundColor: Colors.greyLight, textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Caption color={Colors.greyDark}>No brands linked.</Caption>
        </div>
      )
    }

    return (
      <div className="brandsAccessListContainer">
        {brands.map((brand) => {
          const loadedBrand = loadedBrandsDict[brand.id] // NOTE: we can use loadedBrand to get ALL the properties we might need from brand

          return (
            <div
              key={brand.id}
              className={clsx('brandsListItemContainer', classes.itemContainer)}
            >
              <div className='flex gap-4 items-center'>
                <Avatar src={loadedBrand?.image ?? undefined} sx={{ width: 24, height: 24 }} />
                <Body2
                  className='brandsListItemName'
                  color={Colors.black}
                  style={{ textWrap: 'nowrap', maxWidth: 120, textOverflow: 'ellipsis', overflow: 'hidden' }}
                >
                  {brand.name}
                </Body2>
                {brand.tier === PlanType.PRO &&
                  <UpgradeToProChip
                    className="artistsListsProChip"
                    type="label"
                    withMargin={false}
                  />
                }
              </div>
              <div
                className={clsx(
                  'brandsListOptionsContainer',
                  classes.optionsContainer
                )}
              >
                {renderBrandPermissionSelect(brand)}
                {canEdit && brands.length > 1 &&
                  <TrashIcon
                    className='brandsListTrashIcon'
                    onClick={() => onDeleteBrand(brand.id)}
                    cursor='pointer'
                    height={24}
                    width={24}
                  />
                }
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <div className="brandsListComponent" style={sty.brandsListComponent}>
      <div className="brandsListHeader" style={sty.brandsListHeader}>
        <Body2 color={Colors.black}>
          {isCurrentUser ?
            'You have access to:'
            :
            'This teammate has access to:'
          }
        </Body2>
        {canEdit &&
          <AddBrandsPopUp brands={brands} onAddBrand={onAddBrand} />
        }
      </div>
      {renderBrandsAccessList()}
    </div>
  );
};

const sty: Record<string, CSSProperties> = {
  brandsListComponent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    width: '100%',
  },
  brandsListHeader: {
    display: 'flex',
    justifyContent: 'space-between',
  },
}

export default BrandsList;